import { useCallback, useEffect, useRef, useState } from "react";
import Space from "../../../passapp-sdk/space";
import { SpaceEventsData } from "../../../passapp-sdk/types/spaceTypes";
import { useStore } from "../../../hooks/useStore";
import { todayForSpaceEvents } from "../utils/spaceEventMappers";
import { getCachedSpaceEvents, setCachedSpaceEvents } from "../store/spaceEventsStore";

const space = new Space();

const ERROR_MESSAGE = "No pudimos cargar los ingresos de hoy.";

const POLLING_MS = 15000;

type FetchOptions = { silent?: boolean };

// Ingresos del día del espacio del operador (el espacio sale del token).
const useSpaceEvents = (date: string = todayForSpaceEvents()) => {
  const { authStore } = useStore();
  const mountedRef = useRef(true);
  // Para descartar la respuesta de un pedido lento que llega después de uno más nuevo.
  const requestIdRef = useRef(0);
  const [data, setData] = useState<SpaceEventsData | null>(() =>
    getCachedSpaceEvents(authStore.token, date)
  );
  const [loading, setLoading] = useState(
    () => getCachedSpaceEvents(authStore.token, date) === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchEvents = useCallback(
    (options?: FetchOptions) => {
      const silent = options?.silent === true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      space
        .getSpaceEvents(authStore.token, date)
        .then((response: any) => {
          const events: SpaceEventsData | null = response?.data || null;
          if (events) setCachedSpaceEvents(authStore.token, date, events);
          if (!mountedRef.current || requestId !== requestIdRef.current) return;
          setData(events);
          setError(null);
          setLoading(false);
        })
        .catch((err: any) => {
          console.error(err);
          if (!mountedRef.current || requestId !== requestIdRef.current) return;
          // Un refresh que falla no borra lo que el operador ya tiene en pantalla: puede ser
          // un corte de red de un segundo y el próximo intento entra en POLLING_MS.
          if (silent) return;
          setError(typeof err === "string" && err ? err : ERROR_MESSAGE);
          setLoading(false);
        });
    },
    [authStore.token, date]
  );

  useEffect(() => {
    fetchEvents({ silent: getCachedSpaceEvents(authStore.token, date) !== null });
  }, [fetchEvents, authStore.token, date]);

  // Trae los ingresos que registran otros operadores del mismo espacio; los de este equipo
  // ya entran por el refresh del montaje al volver del resultado.
  useEffect(() => {
    const polling = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      fetchEvents({ silent: true });
    }, POLLING_MS);
    return () => window.clearInterval(polling);
  }, [fetchEvents]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchEvents({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [fetchEvents]);

  return { data, loading, error, refresh: fetchEvents };
};

export default useSpaceEvents;
